import React, { useEffect, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { statisticsApi } from '../../services/statisticsApi';
import type { PlayerGlobalStats, Achievement } from '../../services/statisticsApi';
import './StatisticsDashboard.css';

const StatisticsDashboard: React.FC = () => {
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const [globalStats, setGlobalStats] = useState<PlayerGlobalStats | null>(null);
  const [achievements, setAchievements] = useState<Achievement[]>([]);
  const [loading, setLoading] = useState(true);
  const [levelInfo, setLevelInfo] = useState({ level: 1, xp: 0, nextLevelXp: 100, progress: 0 });
  const [rankTitle, setRankTitle] = useState('🌱 Beginner');

  console.log('[StatisticsDashboard] Component mounted/rendered', {
    authLoading,
    isAuthenticated,
    userId: user?.id || user?._id,
    loading
  });

  useEffect(() => {
    console.log('[StatisticsDashboard] useEffect triggered', {
      authLoading,
      userId: user?.id || user?._id,
      isAuthenticated
    });
    
    // Wait for auth initialization; then either load stats or show unauthenticated state
    if (authLoading) {
      console.log('[StatisticsDashboard] Auth still loading, waiting...');
      return;
    }
    const userId = user?.id || user?._id;
    if (userId) {
      console.log('[StatisticsDashboard] User found, calling loadStatistics for userId:', userId);
      loadStatistics();
    } else {
      console.log('[StatisticsDashboard] No user, showing empty state');
      setGlobalStats(null);
      setAchievements([]);
      setLoading(false);
    }
  }, [user, authLoading]);

  const loadStatistics = async () => {
    try {
      const userId = user?.id || user?._id;
      if (!userId) {
        console.error('[StatisticsDashboard] No userId available');
        return;
      }
      
      console.log('[StatisticsDashboard] loadStatistics called for userId:', userId);
      setLoading(true);
      
      // Get global stats
      console.log('[StatisticsDashboard] About to call statisticsApi.getPlayerGlobalStats...');
      const stats = await statisticsApi.getPlayerGlobalStats(userId);
      console.log('[StatisticsDashboard] Received stats:', stats);
      setGlobalStats(stats);
      
      // Calculate level and rank
      // Prefer profile level/XP if available to keep header consistent with dashboard
      const profileLevel = (user as any)?.level as number | undefined;
      const profileXp = (user as any)?.totalExperience as number | undefined;
      if (typeof profileLevel === 'number' && typeof profileXp === 'number') {
        // When using profile values, progress/nextLevel are informational only
        const nextLevelXp = Math.max(profileXp, (profileLevel + 1) * 1000);
        const prevLevelXp = profileLevel * 1000;
        const progress = Math.max(0, Math.min(100, ((profileXp - prevLevelXp) / Math.max(1, nextLevelXp - prevLevelXp)) * 100));
        setLevelInfo({ level: profileLevel, xp: profileXp, nextLevelXp, progress });
      } else {
        const level = statisticsApi.calculatePlayerLevel(stats);
        setLevelInfo(level);
      }
      
      const rank = statisticsApi.getRankTitle(stats.overallWinRate, (user as any)?.level ?? levelInfo.level);
      setRankTitle(rank);
      
      // Calculate achievements
      const achievementList = statisticsApi.calculateAchievements(stats);
      setAchievements(achievementList);
      
    } catch (error) {
      console.error('[StatisticsDashboard] Error loading statistics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="statistics-dashboard loading">
        <div className="loading-spinner"></div>
        <p>Loading your statistics...</p>
      </div>
    );
  }

  // Unauthenticated or no stats yet
  if (!globalStats) {
    return (
      <div className="statistics-dashboard empty">
        {isAuthenticated ? (
          <div className="empty-state">
            <span className="empty-icon">📊</span>
            <h2>No Statistics Yet</h2>
            <p>Play some games to see your statistics here!</p>
          </div>
        ) : (
          <div className="empty-state">
            <span className="empty-icon">🔐</span>
            <h2>Sign in to view statistics</h2>
            <p>Your personal stats are available after you log in.</p>
            <a
              href="/login"
              className="cta-button"
              style={{
                display: 'inline-block',
                marginTop: '0.75rem',
                padding: '0.5rem 0.9rem',
                borderRadius: 8,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                fontWeight: 600,
                textDecoration: 'none'
              }}
            >
              Go to Login
            </a>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="statistics-dashboard">
      {/* Header with Level and Rank */}
      <div className="stats-header">
        <div className="player-info">
          <div className="player-avatar">
            {user?.username?.[0]?.toUpperCase() || 'P'}
          </div>
          <div className="player-details">
            <h1>{user?.username || 'Player'}</h1>
            <div className="player-rank">{rankTitle}</div>
          </div>
        </div>
        
        <div className="level-card">
          <div className="level-number">Level {(user as any)?.level ?? levelInfo.level}</div>
          <div className="level-progress-bar">
            <div 
              className="level-progress-fill" 
              style={{ width: `${levelInfo.progress}%` }}
            />
          </div>
          <div className="level-xp">
            {((user as any)?.totalExperience ?? levelInfo.xp).toLocaleString()} XP
          </div>
        </div>
      </div>

      {/* Main Statistics Grid */}
      <div className="stats-grid">
        <div className="stat-card games-played">
          <div className="stat-icon">🎮</div>
          <div className="stat-value">{globalStats.totalGamesPlayed}</div>
          <div className="stat-label">Games Played</div>
          <div className="stat-detail">{globalStats.totalRoomsPlayed} rooms</div>
        </div>

        <div className="stat-card games-won">
          <div className="stat-icon">🏆</div>
          <div className="stat-value">{globalStats.totalGamesWon}</div>
          <div className="stat-label">Games Won</div>
          <div className="stat-detail">
            {globalStats.totalGamesPlayed - globalStats.totalGamesWon} losses
          </div>
        </div>

        <div className="stat-card win-rate">
          <div className="stat-icon">📊</div>
          <div className="stat-value">{globalStats.overallWinRate.toFixed(1)}%</div>
          <div className="stat-label">Win Rate</div>
          <div className="stat-detail">
            {globalStats.overallWinRate >= 50 ? 'Above average!' : 'Keep improving!'}
          </div>
        </div>

        <div className="stat-card total-score">
          <div className="stat-icon">💎</div>
          <div className="stat-value">{globalStats.totalScore.toLocaleString()}</div>
          <div className="stat-label">Total Score</div>
          <div className="stat-detail">{globalStats.averageScore.toFixed(0)} avg</div>
        </div>

        <div className="stat-card best-score">
          <div className="stat-icon">⭐</div>
          <div className="stat-value">{globalStats.bestRoomScore}</div>
          <div className="stat-label">Best Game Score</div>
          <div className="stat-detail">Personal record</div>
        </div>

        <div className="stat-card average-score">
          <div className="stat-icon">📈</div>
          <div className="stat-value">{globalStats.averageScore.toFixed(1)}</div>
          <div className="stat-label">Average Score</div>
          <div className="stat-detail">Per game</div>
        </div>
      </div>

      {/* Achievements Section */}
      <div className="achievements-section">
        <h2 className="section-title">
          <span className="title-icon">🎖️</span>
          Achievements
          <span className="achievement-count">{achievements.length}</span>
        </h2>
        
        {achievements.length > 0 ? (
          <div className="achievements-grid">
            {achievements.map((achievement) => (
              <div key={achievement.id} className="achievement-card unlocked">
                <div className="achievement-icon">{achievement.icon}</div>
                <div className="achievement-info">
                  <div className="achievement-name">{achievement.name}</div>
                  <div className="achievement-description">{achievement.description}</div>
                </div>
                <div className="achievement-badge">✓</div>
              </div>
            ))}
          </div>
        ) : (
          <div className="no-achievements">
            <p>Play more games to unlock achievements!</p>
          </div>
        )}
      </div>

      {/* Performance Insights */}
      <div className="insights-section">
        <h2 className="section-title">
          <span className="title-icon">💡</span>
          Performance Insights
        </h2>
        
        <div className="insights-grid">
          {globalStats.overallWinRate >= 60 && (
            <div className="insight-card positive">
              <div className="insight-icon">🔥</div>
              <div className="insight-text">
                You're on fire! Your win rate is {globalStats.overallWinRate.toFixed(0)}%, 
                which is excellent!
              </div>
            </div>
          )}
          
          {globalStats.totalGamesPlayed >= 50 && (
            <div className="insight-card positive">
              <div className="insight-icon">⭐</div>
              <div className="insight-text">
                You're a veteran player with {globalStats.totalGamesPlayed} games played!
              </div>
            </div>
          )}
          
          {globalStats.averageScore > 100 && (
            <div className="insight-card positive">
              <div className="insight-icon">💯</div>
              <div className="insight-text">
                Strong scoring! Your average of {globalStats.averageScore.toFixed(0)} points 
                per game is impressive.
              </div>
            </div>
          )}
          
          {globalStats.overallWinRate < 40 && globalStats.totalGamesPlayed >= 10 && (
            <div className="insight-card neutral">
              <div className="insight-icon">💪</div>
              <div className="insight-text">
                Keep practicing! Focus on bidding strategy and partner coordination 
                to improve your win rate.
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Quick Stats Summary */}
      <div className="summary-section">
        <div className="summary-card">
          <h3>Your Journey</h3>
          <div className="summary-stats">
            <div className="summary-item">
              <span className="summary-label">Games per Room</span>
              <span className="summary-value">
                {(globalStats.totalGamesPlayed / Math.max(1, globalStats.totalRoomsPlayed)).toFixed(1)}
              </span>
            </div>
            <div className="summary-item">
              <span className="summary-label">Win Streak Potential</span>
              <span className="summary-value">
                {Math.floor(globalStats.overallWinRate / 10)} games
              </span>
            </div>
            <div className="summary-item">
              <span className="summary-label">Total Points Earned</span>
              <span className="summary-value">
                {globalStats.totalScore.toLocaleString()}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StatisticsDashboard;
